import {
  Dialog,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { type ChangeEvent, useEffect, useState, type FC } from "react";
import CircleIcon from "@mui/icons-material/Circle";
import { heIL } from "@mui/x-date-pickers/locales";
import { isMobile } from "react-device-detect";
import dayjs, { type Dayjs } from "dayjs";
import { userRepo } from "@repositories";
import clsx from "clsx";

import { type Association, type Status, type User, type UserSkill } from "@entities";
import { useAsyncEffect } from "@shared/frontend/hooks";

import { formatPhoneNumber } from "../../../generic-functions/format-phone-number";
import { getStatusOptions } from "../../../generic-functions/get-status-options.function";
import { openWhatsApp } from "../../../generic-functions/open-whatsapp-message";
import { FormGoogle } from "../../form-components/form-google-autocomplete/FormGoogle";
import { SelectComponentSkills } from "../../select-component/SelectComponentSkills";
import { SelectComponentTeams } from "../../select-component/SelectComponentTeams";
import { UploadImage } from "../../form-components/image-uploader/ImageUploader";
import { useOrganization } from "../../../contexts/OrganizationProvider";
import { useCommunities } from "../../../queries/communities.query";
import { useSkills } from "../../../queries/skills.query";
import { TeamsSelectDialog } from "../../teams-select";
import { Skills } from "../../skills/Skills";
import { VoicenterCallButton } from "../../voicenter/VoicenterCallButton";
import { InputWithLabel } from "../../form-components/InputWithLabel";
import { SelectWithLabel } from "../../form-components/SelectWithLabel";
import "./UserCardPopup.scss";

interface UserCardGeneralInfoProps {
  setUserDetails: React.Dispatch<React.SetStateAction<Partial<Association> | undefined>>;
  userDetails: Partial<Association> | undefined;
  isAdd: boolean;
  userId: string;
}


const GenderSelect: FC<{ userDetails: Partial<Association>; handleUserFieldChange: Function; }> = ({ userDetails, handleUserFieldChange }) => {
  const genderArray = [
    { id: "1", name: "MALE", hebrewName: "זכר" },
    { id: "2", name: "FEMALE", hebrewName: "נקבה" },
    { id: "3", name: "OTHER", hebrewName: "אחר" },
  ];

  return (
    <SelectWithLabel
      label="מגדר"
      value={
        userDetails?.user?.gender === "MALE"
          ? "זכר"
          : userDetails?.user?.gender === "FEMALE"
            ? "נקבה"
            : "אחר"
      }
      onChange={(event) => {
        handleUserFieldChange(
          "gender",
          genderArray.find((item) => item.hebrewName === event.target.value)?.name,
        );
      }}
      options={genderArray.map((item) => ({ id: item.id, name: item.name === "MALE" ? "זכר" : item.name === "FEMALE" ? "נקבה" : "אחר" }))}
    />
  );
};

export const UserCardGeneralInfo: FC<UserCardGeneralInfoProps> = (props) => {
  const { setUserDetails, userDetails = {}, isAdd } = props;
  const [teamsAnchor, setTeamsAnchor] = useState<EventTarget | null>(null);
  const [skillsAnchor, setSkillsAnchor] = useState<EventTarget | null>(null);
  const [userSkills, setUserSkills] = useState<UserSkill[]>([]);
  const [imageId, setImageId] = useState<string>();
  const { skillsAndVehicles } = useSkills();
  const { communities: teams } = useCommunities();
  const [statusOptions, setStatusOptions] = useState<Status[]>([]);
  const { currOrganization } = useOrganization();

  useEffect(() => {
    if (userDetails?.user?.profileImageId) {
      setImageId(userDetails.user?.profileImageId);
    }
  }, [userDetails?.user?.profileImageId]);

  useAsyncEffect(async () => {
    if (!statusOptions.length) {
      const status = await getStatusOptions("VOLUNTEER", currOrganization?.id ?? "");
      setStatusOptions(status);
    }
  }, [currOrganization]);

  function handleUserFieldChange<T extends keyof User>(fieldName: T, value: User[T]) {
    if (!userDetails) return;
    setUserDetails((prev) => ({
      ...prev!,
      user: {
        ...prev?.user,
        [fieldName]: value
      } as User
    }));
  }

  function handleFieldChange<T extends keyof Association>(
    fieldName: T,
    value: Association[T],
  ) {
    if (!userDetails) return;
    setUserDetails((prev) => ({
      ...prev!,
      [fieldName]: value
    }));
  }

  const sx = {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- mui naming
    "& legend": { display: "none" },
    // eslint-disable-next-line @typescript-eslint/naming-convention -- mui naming
    "& fieldset": { top: 0 },
  };

  return (
    <>
      <div className="first-lines">
        <div className="first-lines-inputs">
          <Stack direction="column" spacing={2} width="100%">
            <Stack direction="row" spacing={2} width="90%" alignItems="center">
              <InputWithLabel
                label="שם מלא"
                value={userDetails?.personalDetails?.name || userDetails?.user?.name || ""}
                onChange={(event: ChangeEvent<HTMLInputElement>) => handleUserFieldChange("name", event.target.value)}
              />
              <GenderSelect userDetails={userDetails} handleUserFieldChange={handleUserFieldChange} />
              <LocalizationProvider
                adapterLocale="he"
                localeText={
                  heIL.components.MuiLocalizationProvider.defaultProps.localeText
                }
                dateAdapter={AdapterDayjs}
              >
                <DatePicker
                  label="תאריך לידה"
                  className="input-label-container"
                  disableFuture
                  value={dayjs(userDetails?.user?.dateOfBirth || new Date())}
                  onChange={(newValue: Dayjs | null) =>
                    handleUserFieldChange("dateOfBirth", newValue?.toDate() ?? null)
                  }
                  format="DD/MM/YYYY"
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "0.9rem",
                    },
                    "& .MuiInputBase-input": {
                      width: "7vw",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "0.9rem",
                      color: "rgba(0, 0, 0, 0.7)",
                    },
                  }}
                />
              </LocalizationProvider>
            </Stack>
            <FormControlLabel
              control={
                <Switch
                  checked={!!userDetails?.user?.isVerified}
                  onChange={(event) =>
                    handleUserFieldChange("isVerified", event.target.checked)
                  }
                />
              }
              label="משתמש מאומת (העלאת בקשה עבור מישהו אחר)"
            />
            <Stack direction="row" spacing={2} width="100%">
              <SelectWithLabel
                label="סטטוס פעילות"
                value={
                  userDetails?.activityStatus === "NEW"
                    ? "חדש"
                    : userDetails?.activityStatus === "ACTIVE"
                      ? "פעיל"
                      : userDetails?.activityStatus === "LEGACY"
                        ? "מאגר ישן"
                      : "לא פעיל"
                }
                onChange={(event) => {
                  handleFieldChange(
                    "activityStatus",
                    event.target.value === "חדש"
                      ? "NEW"
                      : event.target.value === "פעיל"
                        ? "ACTIVE"
                        : event.target.value === "מאגר ישן"
                          ? "LEGACY"
                        : "INACTIVE",
                  );
                }}
                options={[
                  { id: "1", name: "חדש" },
                  { id: "2", name: "פעיל" },
                  { id: "3", name: "לא פעיל" },
                  { id: "4", name: "מאגר ישן" },
                ]}
              />
              <SelectWithLabel
                label="סטטוס"
                value={userDetails?.status?.title ?? "ללא סטטוס"}
                onChange={(event) => {
                  if (event.target.value === "ללא סטטוס") {
                    handleFieldChange("status", undefined);
                    return;
                  }
                  const selectedStatus = statusOptions?.find((item) => item.title === event.target.value);
                  if (selectedStatus) {
                    handleFieldChange("status", selectedStatus);
                  }
                }}
                options={[
                  { id: "none", name: "ללא סטטוס" },
                  ...statusOptions.map((item) => ({ id: item.id, name: item.title })),
                ]}
              />
            </Stack>
          </Stack>
        </div>
        <UploadImage
          outOfForm
          name=""
          handleImageChange={(imageId) =>
            handleUserFieldChange("profileImageId", imageId)
          }
          imageId={imageId}
        />
      </div>
      <div className="second-lines">
        <TextField
          variant="outlined"
          label="הערות פנימיות"
          value={userDetails?.comments || ""}
          onChange={(event) => handleFieldChange("comments", event.target.value)}
          multiline
          minRows={3}
          maxRows={6}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={{
            "& .MuiInputBase-root": {
              borderRadius: "0.9rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.9rem",
              color: "rgba(0, 0, 0, 0.7)",
            },
          }}
        />
        <Stack direction="row" spacing={2} width="100%">
          <FormGoogle
            InputProps={{ className: "input" }}
            InputLabelProps={{ className: "label" }}
            onLocationChange={(location) => {
              handleUserFieldChange("address", location.locationName);
              handleUserFieldChange("coordinates", location.latLng);
            }}
            placeValue={userDetails?.user?.address ?? ""}
            label="כתובת"
            sx={{
              "& .MuiInputBase-root": {
                borderRadius: "0.9rem",
              },
              "& .MuiInputLabel-root": {
                fontSize: "0.9rem",
                color: "rgba(0, 0, 0, 0.7)",
              },
            }}
          />
        </Stack>
        <Stack direction="row" spacing={2} width="100%" alignItems="flex-end">
          <InputWithLabel
            label="טלפון נייד"
            value={
              isAdd
                ? userDetails?.user?.phoneNumber?.replace("+972", "0") || ""
                : formatPhoneNumber(userDetails?.user?.phoneNumber)
            }
            disabled={!isAdd}
            onChange={(event) =>
              handleUserFieldChange("phoneNumber", event.target.value)
            }
          />
          <VoicenterCallButton phoneNumber={userDetails?.user?.phoneNumber || ""} />
          <button
            type="button"
            className="whatsapp-button"
            onClick={() => openWhatsApp(userDetails?.user?.phoneNumber || "")}
          >
            <img
              className="whatsapp-image"
              src="/whatsapp.png"
              alt="whatsapp-logo"
            />
          </button>
          <InputWithLabel
            label="אימייל"
            value={userDetails?.personalDetails?.email || userDetails?.user?.email || ""}
            onChange={(event) => handleUserFieldChange("email", event.target.value)}
          />
        </Stack>
        <Stack direction="row" spacing={2} width="100%">
          <InputWithLabel
            label="שעות למלגאים"
            value={userDetails?.scholarshipsHoursNumber || ""}
            onChange={(event) => handleFieldChange("scholarshipsHoursNumber", Number(event.target.value))}
          />
          <InputWithLabel
            label="ת.ז"
            value={userDetails?.personalDetails?.numberID || userDetails?.user?.numberID || ""}
            onChange={(event) =>
              handleUserFieldChange("numberID", event.target.value)
            }
          />
        </Stack>
      </div>
      <div className="teams-and-skills">
        <div className="volunteer-teams-container">
          <SelectComponentSkills
            itemsList={skillsAndVehicles?.skills || []}
            chosenList={
              userDetails?.user?.skills?.length
                ? userDetails?.user?.skills.map((skill) => skill.skillId)
                : (userSkills.map((skill) => skill.skillId) ?? [])
            }
            title="יכולות"
          />
          <button
            className="add-teams-button"
            onClick={(event) => {
              setSkillsAnchor(event.currentTarget);
            }}
          >
            הוספת יכולות
          </button>
        </div>
        <div className="volunteer-teams-container">
          <SelectComponentTeams
            itemsList={teams || []}
            chosenList={
              userDetails?.teams?.map((team) => team.teamId) || [
                teams?.find((team) => team.isMainOrganizationTeam)?.id || "",
              ]
            }
            title="פרויקטים"
          />
          <button
            className="add-teams-button"
            onClick={(event) => setTeamsAnchor(event.currentTarget)}
          >
            הוספת פרויקט
          </button>
          <TeamsSelectDialog
            open={!!teamsAnchor}
            value={
              userDetails?.teams?.map((team) => team.teamId) || [teams?.find(team => team.isMainOrganizationTeam)?.id || ""]
            }
            onClose={() => {
              setTeamsAnchor(null);
            }}
            onSave={(chosenTeams) => {
              handleFieldChange(
                "teams",
                chosenTeams.map((team) => ({
                  teamId: team,
                })) as any,
              );
              setTeamsAnchor(null);
            }
            }
          />
        </div>
      </div>
      {(userDetails?.preferredPositions?.length || 0) > 0 && (
        <div className="positions-container">
          <label>
            העדפות
          </label>
          <div
            className="positions-list"
            style={{
              width: "100%",
              display: "flex",
              flexWrap: "wrap",
              gap: "0.5rem",
            }}
          >
            {userDetails?.preferredPositions?.map((position) => {
              return (
                <div className="position-item" key={position}>
                  {position}
                </div>
              );
            })}
          </div>
        </div>
      )}
      <Dialog
        className="skills-dialog"
        PaperProps={
          isMobile
            ? { sx: { maxWidth: "unset", width: "96%", margin: 0 } }
            : undefined
        }
        open={!!skillsAnchor}
        onClose={() => setSkillsAnchor(null)}
      >
        <Skills
          isProfile
          chosenSkills={userDetails?.user?.skills?.map((skill) => skill.skillId) || []}
          hasVehicle={!!userDetails?.user?.hasVehicle}
          handleSave={async (chosenSkills, hasVehicle) => {
            setSkillsAnchor(null);
            handleUserFieldChange(
              "skills",
              chosenSkills.map((skill) => ({
                skillId: skill,
                userId: userDetails?.user?.id!,
              })),
            );
            handleUserFieldChange("hasVehicle", hasVehicle);
          }}
        />
      </Dialog>
    </>
  );
};
